/**
 * The master mapping table (BUILD_PLAN.md sections 5.2 and 12.1; ADR 0078):
 * every Autotask company, compact, with a status for each service, and a bulk
 * edit of billing type. Clicking anywhere on a client's row, or its arrow,
 * opens the client underneath (ClientDetails; ADR 0082), which edits the
 * rest: Axon's Autotask settings, the ScreenConnect company, the Datto RMM
 * site, the phone numbers and the email domains. The row's own controls, the checkbox and the
 * billing type, don't open it. One client is open at a time. A client you
 * open or change stays in view until you change the filters, so mapping it
 * under "Without a company" or setting its billing type under "Only without
 * a billing type" never makes it vanish (ADR 0078: a mapped value never
 * disappears from view). The table never scrolls sideways
 * (the owner chose this over a column per service, "we have more services to
 * add"): narrower than a laptop, each row is a card, as on Mappings → Techs.
 *
 * Each change saves at once. The API checks it again (owner only, and the
 * default contract must be one of the company's own that can be: active,
 * Recurring Service or Time & Materials, and not ended; ADR 0101).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { ApiError } from "../../api/client";
import type {
  BillingType,
  ClientChanges,
  ClientMapping,
  ClientRecord,
  DattoSites,
  ScreenConnectCompanies,
} from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Panel } from "../../components/ui/Panel";
import { ClientDetails } from "./ClientPanel";
import { CLIENTS_KEY, DATTO_SITES_KEY, SCREENCONNECT_KEY } from "./keys";
import { ChipCell, OpenArrow, OpenedRow } from "./SummaryRow";
import { NOT_MAPPED, type Status, stayClosed, summaryRowClass, useRowToggle } from "./summaryRows";

type TypeFilter = "customers" | "all";
type ScreenConnectFilter = "any" | "unmapped" | "suggested";

/** Autotask's companyType ID for Customer (ADR 0020 section 6). */
const COMPANY_TYPE_CUSTOMER = 1;


// A filter: never wider than the window, though Type's longest choice is
// wider than a phone.
const inputClass =
  "h-10 max-w-full rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

const pillButton =
  "h-9 cursor-pointer rounded-pill border border-line bg-card px-4 font-medium " +
  "hover:bg-brand-tint disabled:cursor-not-allowed disabled:opacity-50";

const BILLING_LABELS: Record<BillingType, string> = { managed: "Managed", hourly: "Hourly" };

/**
 * The API lists only active Customers, Leads, Prospects and Partners; never
 * vendors or inactive companies (the owner's decision, ADR 0020 section 6).
 */
function ofType(client: ClientMapping, types: TypeFilter): boolean {
  return types === "all" || client.company_type === COMPANY_TYPE_CUSTOMER;
}

function countOf(count: number, types: TypeFilter): string {
  const noun =
    types === "customers"
      ? count === 1
        ? "customer"
        : "customers"
      : count === 1
        ? "company"
        : "companies";
  return `${String(count)} ${noun}`;
}

function matches(client: ClientMapping, search: string, onlyWithoutBillingType: boolean): boolean {
  if (onlyWithoutBillingType && client.billing_type !== null) {
    return false;
  }
  const needle = search.trim().toLowerCase();
  return (
    needle === "" ||
    client.name.toLowerCase().includes(needle) ||
    String(client.autotask_company_id) === needle
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.fieldErrors[0]?.message ?? error.message;
  }
  return error instanceof Error ? error.message : "The change was not saved.";
}


/** Each client's ScreenConnect status, from the company list (ADR 0078). */
function screenConnectStatuses(data: ScreenConnectCompanies | undefined): Map<string, Status> | null {
  if (data === undefined) {
    return null;
  }
  const statuses = new Map<string, Status>();
  for (const s of data.suggestions) {
    statuses.set(s.client_id, { label: "Suggested", tone: "todo" });
  }
  for (const c of data.companies) {
    if (c.client_id !== null) {
      statuses.set(c.client_id, { label: "Mapped", tone: "done" });
    }
  }
  return statuses;
}

/** Each client's Datto RMM status (ADR 0080): its one site, or several to pick from. */
function dattoStatuses(data: DattoSites | undefined): Map<string, Status> | null {
  if (data === undefined) {
    return null;
  }
  const statuses = new Map<string, Status>();
  for (const clientId of Object.keys(data.several)) {
    statuses.set(clientId, { label: "Pick one", tone: "todo" });
  }
  for (const site of data.sites) {
    if (site.client_id !== null && !site.missing) {
      statuses.set(site.client_id, { label: "Mapped", tone: "done" });
    }
  }
  return statuses;
}

function phoneStatus(client: ClientMapping): Status {
  return client.phone_numbers.length > 0 ? { label: "Main line", tone: "done" } : NOT_MAPPED;
}

/** How many domains an email can match the client by (ADR 0143, the owner's answer 3). */
function emailStatus(client: ClientMapping): Status {
  const count = client.email_domains.length;
  return count > 0
    ? { label: `${String(count)} ${count === 1 ? "domain" : "domains"}`, tone: "done" }
    : NOT_MAPPED;
}

/** The table's columns, the arrow's included: the open client spans them all. */
const COLUMNS = 8;

/**
 * The row's own fields: --brand-ink rings, which read on an open row's tint
 * in both themes (--brand is 2.75:1 there in dark; the review of rc.15).
 */
const rowInputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-ink";

function ClientRow({
  client,
  selected,
  open,
  screenConnect,
  datto,
  outside,
  details,
  onSelect,
  onOpen,
  onSaved,
}: {
  client: ClientMapping;
  selected: boolean;
  open: boolean;
  screenConnect: Status | null;
  datto: Status | null;
  /** In view only because it was opened or changed: the filters leave it out. */
  outside: boolean;
  /** What opens under the row, while it's open. */
  details: (id: string) => ReactNode;
  onSelect: (selected: boolean) => void;
  onOpen: () => void;
  onSaved: (record: ClientRecord) => void;
}) {
  const { api } = useSession();
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (changes: ClientChanges) => api.updateClient(client.id, changes),
    onSuccess: (record) => {
      setError(null);
      onSaved(record);
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send, saving } = useOneSaveAtATime(save);
  const nameId = `${id}-name`;
  const detailsId = `${id}-details`;

  return (
    <>
      <tr
        // Narrower than a laptop, a row is a card: its checkbox, the client
        // and the arrow, then the billing type and the chips, each named.
        className={summaryRowClass(open)}
        // The mouse's way in is the table body's click (useRowToggle);
        // the arrow is the keyboard's.
        data-client-id={client.id}
      >
        <td className="py-3 pl-2 pr-3 max-lg:shrink-0">
          <input
            type="checkbox"
            aria-label={`Select ${client.name}`}
            checked={selected}
            onClick={stayClosed}
            onChange={(event) => {
              onSelect(event.target.checked);
            }}
            className="size-4 accent-brand focus-visible:outline-brand-ink"
          />
        </td>
        <td className="min-w-0 py-3 pr-3 max-lg:flex-1">
          <span id={nameId} className="font-medium">
            {client.name}
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            <span className="font-mono">{client.autotask_company_id}</span>
            <span>{client.company_type_label}</span>
            {client.is_hipaa ? (
              <span className="rounded-pill bg-hipaa-bg px-2 py-0.5 text-xs font-medium text-hipaa-fg">
                HIPAA
              </span>
            ) : null}
            {client.is_internal ? (
              <span className="rounded-pill bg-panel-2 px-2 py-0.5 text-xs font-medium">Internal</span>
            ) : null}
            {outside ? <span className="italic">No longer matches the filters</span> : null}
          </div>
          {error !== null ? (
            <p role="alert" className="mt-1 text-sm text-status-needs-client-fg">
              Not saved: {error}
            </p>
          ) : null}
        </td>
        <td className="py-3 pr-3 max-lg:order-1 max-lg:basis-full max-lg:pt-0 max-lg:pl-2">
          {/* The column's heading, where there are no columns. */}
          <span aria-hidden="true" className="mb-1 block text-xs text-muted lg:hidden">
            Billing type
          </span>
          <select
            aria-label="Billing type"
            aria-describedby={nameId}
            className={rowInputClass}
            value={(saving ? saving.value.billing_type : client.billing_type) ?? ""}
            onClick={stayClosed}
            onChange={(event) => {
              const value = event.target.value;
              send({ billing_type: value === "" ? null : (value as BillingType) });
            }}
          >
            <option value="">Not set</option>
            <option value="managed">Managed</option>
            <option value="hourly">Hourly</option>
          </select>
        </td>
        {(
          [
            ["ScreenConnect", screenConnect],
            ["Datto RMM", datto],
            ["Phones", phoneStatus(client)],
            ["Email", emailStatus(client)],
          ] as const
        ).map(([service, status]) => (
          // In a card, two to a line on a phone, four on a tablet.
          <ChipCell key={service} name={service} status={status} className="max-sm:basis-1/2 sm:max-lg:basis-1/4" />
        ))}
        <td className="py-2 pr-2 text-right max-lg:w-12">
          <OpenArrow open={open} name={client.name} controls={detailsId} onOpen={onOpen} />
        </td>
      </tr>
      {open ? (
        <OpenedRow columns={COLUMNS} rowClassName="max-lg:block" cellClassName="max-lg:block">
          {details(detailsId)}
        </OpenedRow>
      ) : null}
    </>
  );
}

function ClientTable({ clients }: { clients: ClientMapping[] }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const searchId = useId();
  const typesId = useId();
  const screenConnectId = useId();
  const [search, setSearch] = useState("");
  const [types, setTypes] = useState<TypeFilter>("customers");
  const [onlyWithoutBillingType, setOnlyWithoutBillingType] = useState(false);
  // Health's "Company names to map" links here filtered (ADR 0078).
  const [params] = useSearchParams();
  const [screenConnect, setScreenConnect] = useState<ScreenConnectFilter>(() => {
    const asked = params.get("screenconnect");
    return asked === "unmapped" || asked === "suggested" ? asked : "any";
  });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  // Clients opened or changed since the filters last changed: they stay in
  // view whatever the filters say, so a change never makes its client vanish.
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set());
  const companies = useQuery({
    queryKey: SCREENCONNECT_KEY,
    queryFn: () => api.listScreenConnectCompanies(),
    retry: 1,
  });
  const statuses = screenConnectStatuses(companies.data);
  const sites = useQuery({
    queryKey: DATTO_SITES_KEY,
    queryFn: () => api.listDattoSites(),
    retry: 1,
  });
  const dattos = dattoStatuses(sites.data);
  const statusOf = (client: ClientMapping): Status | null =>
    companies.isError ? null : statuses === null ? null : (statuses.get(client.id) ?? NOT_MAPPED);

  // The count line counts only the chosen type, as the list shows it.
  const inType = useMemo(() => clients.filter((c) => ofType(c, types)), [clients, types]);
  const fits = (c: ClientMapping): boolean => {
    if (!ofType(c, types) || !matches(c, search, onlyWithoutBillingType)) {
      return false;
    }
    if (screenConnect === "any" || statuses === null) {
      return true;
    }
    const label = (statuses.get(c.id) ?? NOT_MAPPED).label;
    return screenConnect === "suggested" ? label === "Suggested" : label !== "Mapped";
  };
  // What the filters pick: counted, and what "Select all shown" selects.
  const matching = clients.filter(fits);
  // What's on screen: that, and the clients kept in view for a change.
  const shown = clients.filter((c) => kept.has(c.id) || fits(c));
  // What a bulk edit changes: the ticked clients the filters pick, never one
  // kept in view only for a change.
  const shownSelected = matching.filter((c) => selected.has(c.id));
  const withoutBillingType = inType.filter((c) => c.billing_type === null).length;
  const onNoClient = (companies.data?.companies ?? []).filter((c) => c.client_id === null).length;

  function toggle(id: string) {
    if (id !== openId) {
      keep([id]);
    }
    setOpenId(id === openId ? null : id);
  }

  // Anywhere on a client's row opens or closes it.
  const { bodyRef, onMouseDown, onClick } = useRowToggle("data-client-id", toggle);

  function keep(ids: readonly string[]) {
    setKept((old) => new Set([...old, ...ids]));
  }

  /** A filter changed: show what it says, and the open client. */
  function refilter() {
    setKept(new Set(openId === null ? [] : [openId]));
  }

  function merge(records: readonly ClientRecord[]) {
    keep(records.map((r) => r.id));
    const byId = new Map(records.map((r) => [r.id, r]));
    queryClient.setQueryData<ClientMapping[]>(CLIENTS_KEY, (old) =>
      old?.map((c) => {
        const record = byId.get(c.id);
        return record ? { ...c, ...record } : c;
      }),
    );
  }

  const bulk = useMutation({
    mutationFn: (billingType: BillingType | null) =>
      api.setBillingType(
        shownSelected.map((c) => c.id),
        billingType,
      ),
    onSuccess: (records) => {
      merge(records);
      setSelected(new Set());
    },
  });

  function select(ids: readonly string[], on: boolean) {
    const next = new Set(selected);
    for (const id of ids) {
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    setSelected(next);
  }

  const allShownSelected = matching.length > 0 && matching.every((c) => selected.has(c.id));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex max-w-full min-w-0 flex-col gap-1">
          <label htmlFor={searchId} className="text-sm font-medium">
            Search
          </label>
          <input
            id={searchId}
            type="search"
            placeholder="Name or company ID"
            className={inputClass}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              refilter();
            }}
          />
        </div>
        <div className="flex max-w-full min-w-0 flex-col gap-1">
          <label htmlFor={typesId} className="text-sm font-medium">
            Type
          </label>
          <select
            id={typesId}
            className={inputClass}
            value={types}
            onChange={(event) => {
              setTypes(event.target.value as TypeFilter);
              refilter();
            }}
          >
            <option value="customers">Customers</option>
            <option value="all">All types (customers, leads, prospects, partners)</option>
          </select>
        </div>
        <div className="flex max-w-full min-w-0 flex-col gap-1">
          <label htmlFor={screenConnectId} className="text-sm font-medium">
            ScreenConnect
          </label>
          <select
            id={screenConnectId}
            className={inputClass}
            value={screenConnect}
            onChange={(event) => {
              setScreenConnect(event.target.value as ScreenConnectFilter);
              refilter();
            }}
          >
            <option value="any">Any</option>
            <option value="unmapped">Without a company</option>
            <option value="suggested">With a suggestion</option>
          </select>
        </div>
        <label className="flex h-10 items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={onlyWithoutBillingType}
            onChange={(event) => {
              setOnlyWithoutBillingType(event.target.checked);
              refilter();
            }}
            className="size-4 accent-brand"
          />
          Only without a billing type
        </label>
        <p role="status" className="pb-2 text-muted">
          {`${String(matching.length)} shown of ${countOf(inType.length, types)} · ${String(withoutBillingType)} without a billing type`}
        </p>
      </div>

      {onNoClient > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-row bg-status-needs-client-bg px-4 py-2 text-status-needs-client-fg">
          <span>
            {`${String(onNoClient)} ScreenConnect ${onNoClient === 1 ? "company isn't" : "companies aren't"} on any client yet.`}
          </span>
          <button
            type="button"
            className={pillButton}
            onClick={() => {
              setScreenConnect("suggested");
              refilter();
            }}
          >
            Show clients with a suggestion
          </button>
        </div>
      ) : null}
      {companies.isError ? (
        <p role="alert" className="text-sm text-muted">
          ScreenConnect&apos;s companies could not be loaded, so its column is empty.
        </p>
      ) : null}

      {shownSelected.length > 0 ? (
        <div
          role="region"
          aria-label="Bulk edit"
          className="flex flex-wrap items-center gap-2 rounded-row bg-brand-tint px-4 py-2"
        >
          <span className="font-medium text-brand-ink-on-tint">
            {`${String(shownSelected.length)} selected: set billing type to`}
          </span>
          {(["managed", "hourly"] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={pillButton}
              disabled={bulk.isPending}
              onClick={() => {
                bulk.mutate(type);
              }}
            >
              {BILLING_LABELS[type]}
            </button>
          ))}
          <button
            type="button"
            className={pillButton}
            disabled={bulk.isPending}
            onClick={() => {
              bulk.mutate(null);
            }}
          >
            Not set
          </button>
          {bulk.error ? (
            <p role="alert" className="text-status-needs-client-fg">
              Not saved: {errorMessage(bulk.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-[999_1_560px]">
          {/* Never sideways (BUILD_PLAN 11.3): a column per service from a
              laptop up, and narrower each row a card (summaryRowClass), with
              only "Select all shown" left of the heading row. */}
          <table className="w-full text-left max-lg:block">
            <thead className="text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted max-lg:block">
              <tr className="max-lg:flex">
                <th scope="col" className="w-8 pb-2 pr-3 max-lg:w-auto max-lg:pl-2">
                  <label className="inline-flex items-center gap-3">
                    <input
                      type="checkbox"
                      aria-label="Select all shown"
                      checked={allShownSelected}
                      onChange={(event) => {
                        select(
                          matching.map((c) => c.id),
                          event.target.checked,
                        );
                      }}
                      className="size-4 accent-brand"
                    />
                    <span aria-hidden="true" className="lg:hidden">
                      Select all shown
                    </span>
                  </label>
                </th>
                {["Client", "Billing type", "ScreenConnect", "Datto RMM", "Phones", "Email"].map((heading) => (
                  <th key={heading} scope="col" className="pb-2 pr-3 max-lg:hidden">
                    {heading}
                  </th>
                ))}
                <th scope="col" className="w-12 pb-2 max-lg:hidden">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody ref={bodyRef} className="max-lg:block" onMouseDown={onMouseDown} onClick={onClick}>
              {shown.map((client) => (
                <ClientRow
                  key={client.id}
                  client={client}
                  selected={selected.has(client.id)}
                  open={client.id === openId}
                  screenConnect={statusOf(client)}
                  datto={
                    sites.isError || dattos === null ? null : (dattos.get(client.id) ?? NOT_MAPPED)
                  }
                  outside={!fits(client)}
                  details={(id) => (
                    <ClientDetails
                      id={id}
                      client={client}
                      clients={clients}
                      companies={companies}
                      sites={sites}
                      onSaved={(record) => {
                        merge([record]);
                      }}
                    />
                  )}
                  onSelect={(on) => {
                    select([client.id], on);
                  }}
                  onOpen={() => {
                    toggle(client.id);
                  }}
                  onSaved={(record) => {
                    merge([record]);
                    // A billing type set by hand in the row is that client's
                    // own: untick it, so a bulk edit can't overwrite it.
                    select([record.id], false);
                  }}
                />
              ))}
            </tbody>
          </table>
          {shown.length === 0 ? (
            <p className="py-6 text-center text-muted">No clients match.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ClientMappingSection() {
  const { api } = useSession();
  const clients = useQuery({
    queryKey: CLIENTS_KEY,
    queryFn: () => api.listClients(),
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) && failures < 2,
  });

  let body;
  if (clients.error) {
    body = (
      <p role="alert" className="text-muted">
        The clients could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!clients.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else if (clients.data.length === 0) {
    body = (
      <p className="text-muted">
        No clients yet. They arrive with the Autotask sync, which runs every night; Owner tools →
        Autotask → Sync Autotask now runs it now.
      </p>
    );
  } else {
    body = <ClientTable clients={clients.data} />;
  }

  return (
    <Panel aria-labelledby="client-mapping-heading">
      <h2 id="client-mapping-heading" className="text-lg font-semibold tracking-tight">
        Clients
      </h2>
      <p className="mt-1 text-muted">
        Every Autotask company, and what each service calls it. Billing type decides the billing
        code and issue type used when logging; HIPAA limits what Axon Time stores about the
        client&apos;s activity. Click a client to see and change everything about it.
      </p>
      <div className="mt-4">{body}</div>
    </Panel>
  );
}
